<template>
	<DjCard v-if="!loading" class="bg-white cursor-pointer" hover>
		<NuxtLink
			v-if="jobProfilePath"
			:to="jobProfilePath"
			target="_blank"
			class="absolute inset-0 z-10"
			:aria-label="job.name ? job.name[locale] : 'Job details'"
			@click="$emit('onClick')"
		/>
		<div class="relative z-20 pointer-events-none">
			<div class="flex justify-between items-start mt-5 mr-5 ml-10">
				<BaseCloudImage
					v-if="job.name"
					custom-class="w-dj-80 h-dj-80 outline outline-1 outline-dj-gray-light rounded-normal -mt-10 overflow-hidden"
					:public-id="
						isShowCompany
							? job.company.logo.cloudinary
							: 'https://res.cloudinary.com/djbs/image/upload//c_scale,h_100,w_100/v1764256649/content/DreamJobs/Logo/dreamjobs-logo-gray'
					"
					:alt="job.name[locale]"
				/>

				<div class="flex justify-end items-center" :class="isShowCompany ? '' : 'pb-11'">
					<BaseTagPill
						v-if="isShowCompany && job.company && job.company.has_gold"
						type="gold"
						size="medium"
						class=""
					>
						{{ langs.card.highlighted[locale] }}
					</BaseTagPill>

					<BaseTagPill v-if="job.is_new" type="red" size="medium" class="ml-2">
						{{ langs.card.new_job[locale] }}
					</BaseTagPill>
					<div class="ml-2 pointer-events-auto" @click.stop="toggleSaved">
						<DjCardSaveJob
							@toggle-saved="toggleSaved"
							:isSaved="isSaved"
							:lang="langs.card"
							:locale="locale"
						></DjCardSaveJob>
					</div>
				</div>
			</div>
			<div class="flex flex-col mt-3 mr-7 ml-10">
				<DjClampedSubtitle
					class="h-11 mb-2 my-auto flex items-center text-left"
					:subtitle="isShowCompany && job.company ? job.company.name : '&nbsp;'"
					:clamp="2"
				/>
				<DjClampedTitle
					v-if="job.name"
					:title="job.name[locale]"
					class="h-dj-70 my-auto flex items-center text-left"
					:clamp="3"
				/>
			</div>
			<div class="flex flex-col mt-3 mr-7 ml-10" v-if="address !== ''">
				{{ address }}
			</div>
			<div class="mt-5 mr-7 mb-3 ml-10 text-left">
				<DjCardJobTags :tags="sortTags()" :visible-tags="3" :lang="locale"></DjCardJobTags>
			</div>
			<div class="flex justify-start mr-7 mb-4 ml-10">
				<NuxtLink
					:to="similarJobsPath"
					class="ml-1 text-xs font-semibold pointer-events-auto text-dj-blue"
					@click="$emit('onSimilarClick')"
				>
					<span>{{ langs.card.similar_jobs[locale] }}</span>
					<span class="material-icons-outlined text-sm ml-1">arrow_forward</span>
				</NuxtLink>
			</div>
			<div class="flex justify-between items-start mt-auto mr-7 mb-7 ml-10 text-left">
				<DjCardSalary
					v-if="job.boxes && job.boxes.job_salary"
					:langs="langs.salary"
					:salary="job.boxes.job_salary.data"
					:locale="locale"
				></DjCardSalary>
				<BaseButton square type="tertiary" class="text-dj-gray group-hover:bg-dj-blue group-hover:text-white">
					<span class="material-icons-outlined">arrow_forward</span>
				</BaseButton>
			</div>
			<div
				v-if="showApplicationDate && job.application_date && job.application_date.length > 0"
				class="flex pt-4 -mt-2 mr-7 mb-7 ml-10 text-left text-sm text-dj-red border-t border-t-dj-gray-light"
			>
				{{ langs.card.application_date[locale] }}:
				{{ new Date(job.application_date).toLocaleDateString(locale) }}
			</div>
		</div>
	</DjCard>
	<!-- Loading state -->
	<DjCard v-else class="bg-white border-dj-light-blue animate-pulse">
		<div class="flex justify-between items-start mt-5 mr-7 ml-10">
			<div
				class="w-dj-80 h-dj-80 outline outline-1 outline-dj-gray-minimal bg-dj-gray-minimal rounded-normal -mt-10 overflow-hidden"
			></div>
			<div class="flex justify-end items-center">
				<div class="w-5 h-5 mb-1 my-auto flex rounded-full bg-dj-gray-minimal"></div>
			</div>
		</div>
		<div class="flex flex-col mt-3 mr-7 ml-10">
			<div class="w-full h-4 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-4 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>
		<div class="flex flex-col mt-2 mr-7 ml-10">
			<div class="w-full h-5 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-5 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-5 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>
		<div class="mt-8 mr-7 mb-3 ml-10 text-left">
			<div class="w-full h-3 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-3 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>

		<div class="flex justify-start mt-4 mr-7 mb-4 ml-10">
			<div class="w-1/2 h-3 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>

		<div class="flex justify-between items-start mt-2.5 mr-7 mb-7 ml-10 text-left">
			<div class="w-3/5">
				<div class="w-full h-4 mt-2 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
				<div class="w-full h-4 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			</div>
			<div class="w-12 h-12 ml-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>
	</DjCard>
</template>
<script lang="ts">
import BaseButton from "~/ui-kit/components/BaseButton.vue";
import BaseCloudImage from "~/ui-kit/components/BaseCloudImage.vue";
import BaseTagPill from "~/ui-kit/components/BaseTagPill.vue";

import DjCard from "./DjCard.vue";
import DjCardJobTags from "./partials/DjCardJobTags.vue";
import DjCardSalary from "./partials/DjCardSalary.vue";
import DjCardSaveJob from "./partials/DjCardSaveJob.vue";
import DjClampedTitle from "./partials/DjClampedTitle.vue";
import DjClampedSubtitle from "./partials/DjClampedSubtitle.vue";

import type { JobCardsLang } from "~/ui-kit/types/types";
import type { JobData } from "~/types/job";
import { defineComponent, type PropType, computed } from "vue";
import { getJobSlug } from "~/composables/useJob";

export default defineComponent({
	name: "DjCardJob",
	components: {
		BaseButton,
		BaseCloudImage,
		DjCard,
		DjCardJobTags,
		DjCardSalary,
		DjCardSaveJob,
		DjClampedTitle,
		DjClampedSubtitle,
		BaseTagPill,
	},
	props: {
		job: {
			type: Object,
			default: () => {
				return {};
			},
		},
		locale: {
			type: String,
			default: "hu",
		},
		langs: {
			type: Object as PropType<JobCardsLang>,
			default: () => {
				return {};
			},
		},
		loading: {
			type: Boolean,
			default: false,
		},
		isSaved: {
			type: Boolean,
			default: false,
		},
		showApplicationDate: {
			type: Boolean,
			default: false,
		},
		address: {
			type: String,
			default: "",
		},
	},
	emits: ["toggleSaved", "onClick", "onSimilarClick"],
	setup(props, { emit }) {
		const isShowCompany = computed(() => props.job.company_id !== null);
		const jobProfilePath = computed(() => {
			const companySlug = props.job?.company_id ? props.job?.company?.slug : "_hidden-company_";
			const jobSlug = getJobSlug(props.job as JobData, props.locale);

			if (!jobSlug) return "";
			return `/${props.locale}/job/${companySlug}/${jobSlug}`;
		});

		const similarJobsPath = computed(() => {
			const query = new URLSearchParams();
			const categoryId = props.job?.tags?.job_category?.id;
			const locationId = props.job?.tags?.office_location?.data?.[0]?.id;
			const typeId = props.job?.tags?.job_type?.data?.[0]?.id;
			const levelId = props.job?.tags?.job_level?.id;

			if (categoryId) query.set("category", String(categoryId));
			if (locationId) query.set("location", String(locationId));
			if (typeId) query.set("type", String(typeId));
			if (levelId) query.set("level", String(levelId));

			const queryString = query.toString();
			return queryString ? `/${props.locale}/jobs?${queryString}` : `/${props.locale}/jobs`;
		});

		function toggleSaved() {
			emit("toggleSaved");
		}

		function sortTags() {
			const pattern = { job_level: 1, job_type: 2, job_category: 3, job_subcategory: 4, office_location: 5 };
			return Object.keys(props.job.tags)
				.sort((x, y) => {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					//@ts-ignore
					return pattern[x] - pattern[y];
				})
				.reduce(
					(acc, key) => ({
						...acc,
						[key]: props.job.tags[key],
					}),
					{}
				);
		}

		return {
			sortTags,
			toggleSaved,
			isShowCompany,
			jobProfilePath,
			similarJobsPath,
		};
	},
});
</script>
